/**
 * Пул водителя: «Забрать» с карты, свой пул, смена.
 *
 * Только для роли `scout` (и staff): партнёру сервер отвечает 403, и
 * интерфейс пула ему не показывается вовсе (`lib/auth.tsx`).
 *
 * ⚠ ЗДЕСЬ ДВЕ МУТАЦИИ ИДУТ МИМО ОЧЕРЕДИ, ХОТЯ ПРАВИЛО CLAUDE.md — «ВСЁ ЧЕРЕЗ
 * outbox». «Забрать» и «Закончить смену» — не отметка факта, а вопрос к
 * серверу, ответ на который нужен сейчас: какие байки он взял (и почему
 * отказал остальным), в каком порядке ехать, что вошло в отчёт. Досланный
 * через час «Забрать» снял бы с аренды байки, за которыми водитель уже не
 * едет. Поэтому без сети они честно не работают.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, NetworkError, request } from '@/lib/http'
import type { LatLng } from '@/lib/geo'
import { poolCreateBody } from '@/lib/pool'
import { drainOutbox, enqueue, readOutbox, type OutboxEntry } from '@/lib/offline/outbox'
import { VQK } from './vehicles'
import { optionalNested } from './nested'

export const poolItemSchema = z.object({
  vehicleId: z.string().default(''),
  number: z.string().default(''),
  qrToken: z.string().default(''),
  /** 1..N — порядок объезда, пока state=pooled; 0 у остальных. */
  position: z.number().default(0),
  /** pooled | picked | delivered | dropped | forgotten | removed | unfinished */
  state: z.string().default(''),
  prevStatus: z.string().default(''),
  /** -1 — неизвестно. Опущенное поле — это 0, то есть севший, а не «не знаю». */
  batteryPct: z.number().default(0),
  /** Последняя известная точка байка. */
  lat: z.number().default(0),
  lng: z.number().default(0),
  addedAt: z.string().default(''),
  pickedAt: z.string().default(''),
  closedAt: z.string().default(''),
  /** Только у delivered: line | charge | broken. */
  result: z.string().default(''),
  vehicleStatus: z.string().default(''),
  /** unload (в хаб) | base; пусто у старого бэкенда — читается как база. */
  destination: z.string().default(''),
  /** Хаб, куда едет байк «в хаб»; пусто у «на базу» и у старого сервера. */
  hubId: z.string().default(''),
  hubName: z.string().default(''),
  hubLat: z.number().default(0),
  hubLng: z.number().default(0),
})
export type PoolItem = z.infer<typeof poolItemSchema>

/**
 * Остановка маршрута в порядке езды (сервер, `fleet/internal/biz/pool_plan.go`):
 * pickup — забрать байк, hub — выгрузить в хаб, base — выгрузить на базе.
 * `loadBefore`/`loadAfter` — сколько в машине до и после.
 */
export const poolStopSchema = z.object({
  kind: z.string().default(''),
  vehicleIds: z.array(z.string()).default([]),
  hubId: z.string().default(''),
  name: z.string().default(''),
  lat: z.number().default(0),
  lng: z.number().default(0),
  loadBefore: z.number().default(0),
  loadAfter: z.number().default(0),
})
export type PoolStop = z.infer<typeof poolStopSchema>

const shiftSchema = z.object({
  id: z.string().default(''),
  driverId: z.string().default(''),
  startedAt: z.string().default(''),
  endedAt: z.string().default(''),
})
export type Shift = z.infer<typeof shiftSchema>

/** Нет открытой смены — сервер отвечает `{}`: смены нет, пунктов нет. */
export const poolReplySchema = z.object({
  shift: optionalNested(shiftSchema),
  /** Активные: pooled (по position) и picked. */
  items: z.array(poolItemSchema).default([]),
  /** Только у «Забрать». */
  rejected: z
    .array(z.object({ vehicleId: z.string().default(''), reason: z.string().default('') }))
    .default([]),
  /** Маршрут активных; пусто у старого сервера и когда хабы не прочитались. */
  stops: z.array(poolStopSchema).default([]),
  /** Сколько машина везёт одновременно; 0 — старый сервер (`CAR_CAPACITY`). */
  carCapacity: z.number().default(0),
  /** С какого заряда байк с базы везут в хаб; 0 — старый сервер. */
  baseChargedMinPct: z.number().default(0),
})
export type PoolReply = z.infer<typeof poolReplySchema>

export const shiftReportSchema = z.object({
  shift: optionalNested(shiftSchema),
  charged: z.array(poolItemSchema).default([]),
  broken: z.array(poolItemSchema).default([]),
  onLine: z.array(poolItemSchema).default([]),
  forgotten: z.array(poolItemSchema).default([]),
  dropped: z.array(poolItemSchema).default([]),
  inCar: z.array(poolItemSchema).default([]),
})
export type ShiftReport = z.infer<typeof shiftReportSchema>

/**
 * Свой ключ, а не `QK.pool` из `api/scout.ts`: тот — пул ЗАДАЧ диспетчера,
 * это — пул водителя. Одно слово, две разные вещи.
 */
export const PQK = { mine: ['scout.driverPool'] as const }

/** Номер с наклейки для пункта пула. */
export function poolItemNumber(i: Pick<PoolItem, 'number' | 'qrToken'>): string {
  return i.number || i.qrToken
}

/**
 * Отказ «Забрать» / «Закончить смену» словами.
 *
 * Обе ручки только онлайн, поэтому «нет сети» здесь — штатный ответ, а не
 * сбой, и говорить о нём надо так, чтобы водитель понял, что делать.
 */
const POOL_ERROR_TEXT: Record<string, string> = {
  POOL_EMPTY: 'Не выбрано ни одного байка',
  SHIFT_NOT_OPEN: 'Смена уже закрыта',
  STAFF_REQUIRED: 'Пул доступен только водителям',
}

/**
 * В очереди остались отметки пула — смену закрывать нельзя.
 *
 * Сервер закрывает смену по своему снимку: неотправленный «Поставил» он
 * не увидит, байк уйдёт в «забытые» (или «в машине»), а сама отметка потом
 * вернётся 404 POOL_ITEM_NOT_FOUND. Отчёт разошёлся бы с тем, что водитель
 * сделал руками.
 */
export class PoolPendingError extends Error {
  constructor(readonly count: number) {
    super('В очереди остались отметки пула')
    this.name = 'PoolPendingError'
  }
}

/**
 * Ручек пула на сервере ещё нет (бэкенд не выкачен).
 *
 * По контракту GET, «Забрать» и «Закончить смену» не отвечают 404 вовсе:
 * «смены нет» — это `{}`, а 404 у отметок (POOL_ITEM_NOT_FOUND) идёт через
 * очередь, не сюда. Значит, 404 здесь — только «маршрута не существует».
 */
export function isPoolUnavailable(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404
}

export function poolErrorText(e: unknown): string {
  if (isPoolUnavailable(e)) return 'Пул пока недоступен'
  if (e instanceof PoolPendingError) {
    // Очередь уходит по порядку: застрявшая чужая запись держит и пульные.
    return `Сначала дождитесь отправки отметок пула (${e.count}) — нужна сеть; застрявшую запись в очереди можно снять`
  }
  if (e instanceof NetworkError) return 'Нет сети — пул работает только онлайн'
  if (e instanceof ApiError) return POOL_ERROR_TEXT[e.reason] ?? (e.message || 'Сервер не принял запрос')
  return e instanceof Error ? e.message : 'Не удалось'
}

/**
 * Свой пул.
 *
 * ⚠ ПОСЛЕ 404 ОПРОС ЗАМОЛКАЕТ. Пока бэкенд не выкачен, карта иначе слала бы
 * 404 (и повтор к нему) каждые 20 с у каждого водителя. Возобновляет его
 * любое ручное чтение — открытие карты или карточки, «Обновить», включение
 * режима «Пул»: удачный ответ снимает ошибку, и интервал возвращается.
 */
export function useMyPool(opts: { enabled: boolean; refetchInterval?: number }) {
  const interval = opts.refetchInterval
  return useQuery({
    queryKey: PQK.mine,
    queryFn: () => request('/v1/scout/pool', poolReplySchema),
    enabled: opts.enabled,
    staleTime: 30_000,
    // Повтор один — как у остальных запросов (`app/providers.tsx`), но не на 404.
    retry: (failures, e) => !isPoolUnavailable(e) && failures < 1,
    ...(interval === undefined
      ? {}
      : {
          refetchInterval: (query: { state: { error: unknown } }) =>
            isPoolUnavailable(query.state.error) ? false : interval,
        }),
  })
}

export function createPool(
  vehicleIds: readonly string[],
  me: LatLng | null,
  destinations?: Readonly<Record<string, 'unload' | 'base'>>,
): Promise<PoolReply> {
  return request('/v1/scout/pool', poolReplySchema, {
    method: 'POST',
    body: poolCreateBody(vehicleIds, me, destinations),
  })
}

/**
 * «Забрать». Ответ — новый пул целиком: кладём его в кэш сразу, чтобы номера
 * маршрута появились без второго запроса. Статусы байков сменились на
 * сервере (`in_pool`) — парк перечитываем.
 */
export function useCreatePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      vehicleIds,
      me,
      destinations,
    }: {
      vehicleIds: readonly string[]
      me: LatLng | null
      destinations?: Readonly<Record<string, 'unload' | 'base'>>
    }) => createPool(vehicleIds, me, destinations),
    onSuccess: (reply) => {
      qc.setQueryData<PoolReply>(PQK.mine, { ...reply, rejected: [] })
      void qc.invalidateQueries({ queryKey: VQK.vehicles })
    },
  })
}

/** Отметки пула, которые идут через очередь. */
export type PoolActionKind = 'pool_pick' | 'pool_deliver' | 'pool_drop'
const POOL_KINDS = new Set<string>(['pool_pick', 'pool_deliver', 'pool_drop'])

/**
 * «Забрал» / «Поставил» / «Убрать из пула» — через очередь, как всё
 * остальное: водитель стоит у байка, сети там может не быть. Ключ
 * идемпотентности — id записи. Пул перечитывается после досылки
 * (`app/providers.tsx`), а до неё кнопки держит очередь (`effectivePoolState`).
 */
export function usePoolAction() {
  return useMutation({
    mutationFn: ({
      kind,
      vehicleId,
      payload,
    }: {
      kind: PoolActionKind
      vehicleId: string
      payload: Record<string, unknown>
    }) => enqueue(kind, vehicleId, payload),
  })
}

/**
 * «Закончить смену»: сначала досылка, потом закрытие.
 *
 * `rejected` — отметки, которые сервер отверг во время этой досылки: их
 * обязан показать экран, иначе они пропадут молча (так же, как в
 * `flushOutbox`).
 */
export async function endShift(
  me: LatLng | null,
): Promise<{ report: ShiftReport; rejected: OutboxEntry[] }> {
  const rejected = await drainOutbox()
  const pending = (await readOutbox()).filter((e) => POOL_KINDS.has(e.kind))
  if (pending.length > 0) throw new PoolPendingError(pending.length)
  const report = await request('/v1/scout/shift/end', shiftReportSchema, {
    method: 'POST',
    body: me ? { lat: me.lat, lng: me.lng } : {},
  })
  return { report, rejected }
}

export function useEndShift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (me: LatLng | null) => endShift(me),
    onSettled: () => {
      // Отчёт живёт в `data` мутации: после закрытия GET пула отдаёт `{}`.
      void qc.invalidateQueries({ queryKey: PQK.mine })
      void qc.invalidateQueries({ queryKey: VQK.vehicles })
    },
  })
}
